import { useState, useMemo, useEffect } from 'react';
import useLocale from '../../../hooks/useLocale';
import useCalendarEventsQuery from '../../Calendar/hooks/useCalendarEventsQuery';
import { CalendarEvent } from '../../../api/calendarEvent';

export const useCalendarEventWidget = () => {
  const { data: locale } = useLocale();
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (locale?.country && !hasInitialized) {
      setSelectedCountry(locale.country);
      setHasInitialized(true);
    }
  }, [locale?.country, hasInitialized]);

  const {
    data: calendarEvents,
    isLoading,
    error,
  } = useCalendarEventsQuery({
    country: selectedCountry,
    year: currentYear,
  });

  const nextCalendarEvents = useMemo(() => {
    if (!calendarEvents) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingCalendarEvents = calendarEvents.filter(
      (calendarEvent: CalendarEvent) => {
        const calendarEventDate = new Date(
          calendarEvent.date.year,
          calendarEvent.date.month - 1,
          calendarEvent.date.day,
        );
        calendarEventDate.setHours(0, 0, 0, 0);
        return calendarEventDate >= today;
      },
    );

    return upcomingCalendarEvents
      .sort((a: CalendarEvent, b: CalendarEvent) => {
        const dateA = new Date(a.date.year, a.date.month - 1, a.date.day);
        const dateB = new Date(b.date.year, b.date.month - 1, b.date.day);
        return dateA.getTime() - dateB.getTime();
      })
      .slice(0, 3);
  }, [calendarEvents]);

  const onCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
  };

  return {
    nextCalendarEvents,
    isLoading,
    error,
    selectedCountry,
    onCountryChange,
  };
};
